import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { css, useTheme, jsx, ClassNames } from '@emotion/react';
import { math } from 'polished';
import { textStyles, BodyText, CaptionText } from '@paypalcorp/pp-react-text';
import { utilStyles, uniqueId } from '@paypalcorp/pp-react-utils';
import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { forwardRef, useMemo } from 'react';
import { CriticalIcon, CheckmarkBackgroundIcon, CheckmarkSmIcon } from '@paypalcorp/pp-react-icons';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import { Link } from '@paypalcorp/pp-react-links';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11, _templateObject12, _templateObject13, _templateObject14, _templateObject15, _templateObject16, _templateObject17, _templateObject18, _templateObject19, _templateObject20, _templateObject21, _templateObject22, _templateObject23, _templateObject24, _templateObject25, _templateObject26, _templateObject27, _templateObject28, _templateObject29, _templateObject30, _templateObject31, _templateObject32;
function styles(tokens) {
  var _utilStyles = utilStyles(tokens),
      screenReader = _utilStyles.screenReader;
  /* istanbul ignore next */


  if (!Object.keys(tokens).length) {
    // eslint-disable-next-line no-console
    console.warn('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysBorderRadiusSm = tokens.sysBorderRadiusSm,
      sysBorderThicknessNormal = tokens.sysBorderThicknessNormal,
      sysSizeCircleXs = tokens.sysSizeCircleXs,
      sysColorBackgroundSurface = tokens.sysColorBackgroundSurface,
      sysColorSelectedContrast = tokens.sysColorSelectedContrast,
      sysBorderThicknessEmphasis = tokens.sysBorderThicknessEmphasis,
      sysColorStructureBorderHighContrast = tokens.sysColorStructureBorderHighContrast,
      sysColorSelectedMain = tokens.sysColorSelectedMain,
      sysColorErrorMain = tokens.sysColorErrorMain,
      sysColorSuccessMain = tokens.sysColorSuccessMain,
      sysColorBackgroundMediumContrast = tokens.sysColorBackgroundMediumContrast,
      sysSpaceXs = tokens.sysSpaceXs,
      sysSpaceMd = tokens.sysSpaceMd,
      sysSpaceTiny = tokens.sysSpaceTiny,
      sysSizeIconXs = tokens.sysSizeIconXs,
      sysSizeIconLg = tokens.sysSizeIconLg,
      sysSpaceSm = tokens.sysSpaceSm,
      sysBorderRadiusXs = tokens.sysBorderRadiusXs,
      sysSpaceXl = tokens.sysSpaceXl;
  var checkboxItemTextSpacing = math("".concat(sysSizeCircleXs, " + ").concat(sysSpaceMd, " + ").concat(sysSpaceTiny)); // tokens not available so created variables

  var _textStyles = textStyles(tokens),
      textBody = _textStyles.body,
      textCaption = _textStyles.caption;

  var _utilStyles2 = utilStyles(tokens),
      focusStyles = _utilStyles2.focusStyles;

  var focusAfter = focusStyles.focusAfter,
      stateErrorFocusBorderColorOuter = focusStyles.stateErrorFocusBorderColorOuter,
      stateFocusBorderColorInner = focusStyles.stateFocusBorderColorInner,
      stateFocusBorderWidthInner = focusStyles.stateFocusBorderWidthInner,
      stateErrorFocusBorderColorInner = focusStyles.stateErrorFocusBorderColorInner;

  var labelCheckedCommon = function () {
    return css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n      border-color: ", ";\n      background-color: ", ";\n      color: ", ";\n      border-radius: ", ";\n    "])), sysColorSelectedMain, sysColorSelectedMain, sysColorSelectedContrast, sysBorderRadiusSm);
  }; // Checkbox base class


  var checkboxBase = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: checkbox_base;\n    position: relative;\n    text-align: start;\n    margin-bottom: 0;\n  "]))); // Checkbox group class

  var checkboxGroup = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: checkbox_group;\n    display: flex;\n    flex-direction: column;\n    margin: 0;\n    padding: 0;\n    border: none;\n    text-align: start;\n  "]))); // Checkbox inside group class

  var groupBase = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: group_base;\n    padding: ", " 0;\n  "])), sysSpaceMd); // Checkbox label class

  var checkboxLabel = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: label;\n    -moz-osx-font-smoothing: grayscale;\n    cursor: pointer;\n    display: block;\n    padding-inline-start: ", ";\n    ", "\n    position: relative;\n    text-align: start;\n    span:first-of-type {\n      inset-inline-start: 0;\n    }\n    -webkit-tap-highlight-color: transparent;\n  "])), checkboxItemTextSpacing, textBody());
  var groupLabel = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: group_label;\n    ", "\n    padding: 0 0 ", " 0;\n    margin-bottom: ", ";\n    text-align: start;\n  "])), textBody({
    color: sysColorBackgroundMediumContrast
  }), sysSpaceMd, sysSpaceXs); // Checkbox icon label class

  var iconLabel = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: icon_label;\n    position: relative;\n    top: 0.125rem;\n    margin-left: ", ";\n  "])), sysSpaceMd); // Checkbox icon container class

  var checkIconContainer = function (isDecoration) {
    return css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: checkmark_container;\n    border-radius: ", ";\n    border: ", " solid\n      ", ";\n    box-sizing: border-box;\n    height: ", ";\n    left: 0;\n    position: absolute;\n    top: ", ";\n    width: ", ";\n    line-height: ", ";\n    text-align: center;\n    background-color: ", ";\n    display: flex;\n    justify-content: center;\n    align-items: center;\n  "])), sysBorderRadiusSm, sysBorderThicknessNormal, sysColorStructureBorderHighContrast, sysSizeCircleXs, isDecoration ? 'auto' : 0, sysSizeCircleXs, 1.3, sysColorBackgroundSurface);
  };

  var checkIcon = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: check_icon;\n    display: none;\n    font-size: ", ";\n  "])), sysSizeIconXs);
  var partialIcon = css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    label: partial_icon;\n    height: ", ";\n    width: ", ";\n    background-color: ", ";\n    border-radius: ", ";\n  "])), sysSpaceTiny, "0.875rem", sysColorSelectedContrast, sysBorderRadiusXs);
  var checkboxInput = css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    label: checkbox_input;\n    cursor: pointer;\n    opacity: 0;\n    position: absolute;\n    &:checked + label > span > span:first-of-type {\n      ", "\n      span:first-of-type {\n        display: inline;\n      }\n    }\n    &:focus + label > span > span:first-of-type:after {\n      ", "\n      [dir='rtl'] & {\n        left: 0;\n        right: -", ";\n      }\n    }\n  "])), labelCheckedCommon(), focusAfter({
    radius: sysBorderRadiusSm,
    width: sysSizeCircleXs,
    height: sysSizeCircleXs,
    top: "-".concat(sysBorderThicknessNormal),
    left: "-".concat(sysBorderThicknessNormal),
    border: "".concat(sysBorderThicknessEmphasis, " solid ").concat(stateFocusBorderColorInner)
  }), sysBorderThicknessNormal); // Checkbox error class

  var checkboxErrorState = css(_templateObject12 || (_templateObject12 = _taggedTemplateLiteral(["\n    label: error;\n    &:checked + label > span > span:first-of-type {\n      border-width: ", ";\n      border-color: ", ";\n    }\n    &:focus + label > span > span:first-of-type:after {\n      ", ";\n      [dir='rtl'] & {\n        left: 0;\n        right: -", ";\n      }\n    }\n  "])), sysBorderThicknessEmphasis, sysColorErrorMain, focusAfter({
    radius: sysBorderRadiusSm,
    width: sysSizeCircleXs,
    height: sysSizeCircleXs,
    border: "".concat(stateFocusBorderWidthInner, " solid\n              ").concat(stateErrorFocusBorderColorInner),
    boxShadowColor: stateErrorFocusBorderColorOuter,
    top: "-".concat(sysBorderThicknessEmphasis),
    left: "-".concat(sysBorderThicknessEmphasis)
  }), sysBorderThicknessEmphasis);
  var errorCheckIconContainer = css(_templateObject13 || (_templateObject13 = _taggedTemplateLiteral(["\n    label: error_checkbox;\n    border-width: ", ";\n    border-color: ", ";\n    line-height: ", ";\n  "])), sysBorderThicknessEmphasis, sysColorErrorMain, 1.1);
  var checkboxMessageText = css(_templateObject14 || (_templateObject14 = _taggedTemplateLiteral(["\n    label: error_text;\n    ", "\n    display: flex;\n    align-items: center;\n    margin: 0;\n  "])), textCaption({
    color: sysColorBackgroundMediumContrast
  }));
  var errorIcon = css(_templateObject15 || (_templateObject15 = _taggedTemplateLiteral(["\n    label: error_icon;\n    color: ", ";\n    margin-inline-end: ", ";\n  "])), sysColorErrorMain, sysSpaceXs);
  var successIcon = css(_templateObject16 || (_templateObject16 = _taggedTemplateLiteral(["\n    label: success_icon;\n    color: ", ";\n    margin-inline-end: ", ";\n  "])), sysColorSuccessMain, sysSpaceXs);
  var labelError = css(_templateObject17 || (_templateObject17 = _taggedTemplateLiteral(["\n    padding-bottom: ", ";\n  "])), sysSpaceMd);
  var helper = css(_templateObject18 || (_templateObject18 = _taggedTemplateLiteral(["\n    label: helper_text;\n    ", "\n    margin: ", " 0 0;\n  "])), textCaption({
    color: sysColorBackgroundMediumContrast
  }), sysSpaceXs);
  var groupHelper = css(_templateObject19 || (_templateObject19 = _taggedTemplateLiteral(["\n    label: group_helper_text;\n    margin: 0;\n  "])));
  var checkboxItems = css(_templateObject20 || (_templateObject20 = _taggedTemplateLiteral(["\n    label: checkbox_items;\n    display: flex;\n    align-items: center;\n    min-height: ", ";\n    margin: 0;\n  "])), "1.5rem");
  var itemThumbnail = css(_templateObject21 || (_templateObject21 = _taggedTemplateLiteral(["\n    label: thumbnail;\n    display: flex;\n    align-items: center;\n    margin-inline-end: ", ";\n    min-height: auto;\n    pointer-events: none;\n  "])), sysSpaceSm);
  var itemThumbnailDecoration = css(_templateObject22 || (_templateObject22 = _taggedTemplateLiteral(["\n    label: thumbnail_decoration;\n    width: ", ";\n    height: ", ";\n  "])), sysSizeIconLg, sysSizeIconLg);
  var itemText = css(_templateObject23 || (_templateObject23 = _taggedTemplateLiteral(["\n    label: item_text;\n    margin: 0; /* overwrite default margin */\n    pointer-events: none;\n  "])));
  var itemSecondaryText = css(_templateObject24 || (_templateObject24 = _taggedTemplateLiteral(["\n    label: item_secondary_text;\n    display: block;\n    padding-top: ", ";\n    pointer-events: none;\n    ", ";\n  "])), sysSpaceXs, textCaption({
    color: sysColorBackgroundMediumContrast
  }));
  var groupBaseWithSecText = css(_templateObject25 || (_templateObject25 = _taggedTemplateLiteral(["\n    label: group_base_with_sec_text;\n    padding: ", " 0;\n  "])), sysSpaceSm);
  var legalConsentLabel = css(_templateObject26 || (_templateObject26 = _taggedTemplateLiteral(["\n    label: legal_consent_label;\n    margin-bottom: ", ";\n    > span:first-of-type {\n      display: flex;\n    }\n  "])), sysSpaceMd);
  var legalConsentText = css(_templateObject27 || (_templateObject27 = _taggedTemplateLiteral(["\n    label: legal_consent_text;\n    ", ";\n  "])), textCaption({
    color: sysColorBackgroundMediumContrast
  }));
  var legalConsentSupportingTextWrapper = css(_templateObject28 || (_templateObject28 = _taggedTemplateLiteral(["\n    label: legal_consent_supporting_text_wrapper;\n    padding-inline-start: ", ";\n    ", ";\n    margin: 0;\n  "])), checkboxItemTextSpacing, textCaption({
    color: sysColorBackgroundMediumContrast
  }));
  var legalConsentSupportingTextWrapperWhenNoLabel = css(_templateObject29 || (_templateObject29 = _taggedTemplateLiteral(["\n    label: legal_consent_supporting_text_wrapper_when_no_label;\n    padding-inline-start: 0;\n  "])));
  var legalConsentLinksWrapper = css(_templateObject30 || (_templateObject30 = _taggedTemplateLiteral(["\n    label: legal_consent_links_wrapper;\n    padding: ", " 0 0 0;\n    margin: 0;\n    list-style-type: none;\n  "])), sysSpaceMd);
  var legalConsentLinksWrapperWhenNoLabel = css(_templateObject31 || (_templateObject31 = _taggedTemplateLiteral(["\n    label: legal_consent_links_wrapper_when_no_label;\n    padding-top: 0;\n  "])));
  var legalConsentGroupSpacing = css(_templateObject32 || (_templateObject32 = _taggedTemplateLiteral(["\n    label: legal_consent_group_spacing;\n    display: flex;\n    flex-direction: column;\n    row-gap: ", ";\n    align-items: flex-start;\n  "])), sysSpaceXl);
  return {
    checkboxBase: checkboxBase,
    checkboxGroup: checkboxGroup,
    groupBase: groupBase,
    checkboxLabel: checkboxLabel,
    groupLabel: groupLabel,
    iconLabel: iconLabel,
    checkIconContainer: checkIconContainer,
    checkIcon: checkIcon,
    checkboxInput: checkboxInput,
    checkboxErrorState: checkboxErrorState,
    errorCheckIconContainer: errorCheckIconContainer,
    checkboxMessageText: checkboxMessageText,
    errorIcon: errorIcon,
    partialIcon: partialIcon,
    successIcon: successIcon,
    labelError: labelError,
    helper: helper,
    groupHelper: groupHelper,
    checkboxItems: checkboxItems,
    itemThumbnail: itemThumbnail,
    itemThumbnailDecoration: itemThumbnailDecoration,
    itemText: itemText,
    itemSecondaryText: itemSecondaryText,
    groupBaseWithSecText: groupBaseWithSecText,
    screenReader: screenReader,
    legalConsentLabel: legalConsentLabel,
    legalConsentText: legalConsentText,
    legalConsentSupportingTextWrapper: legalConsentSupportingTextWrapper,
    legalConsentSupportingTextWrapperWhenNoLabel: legalConsentSupportingTextWrapperWhenNoLabel,
    legalConsentLinksWrapper: legalConsentLinksWrapper,
    legalConsentGroupSpacing: legalConsentGroupSpacing,
    legalConsentLinksWrapperWhenNoLabel: legalConsentLinksWrapperWhenNoLabel
  };
}

var _excluded = ["id", "label", "aria-label", "decoration", "secondaryText", "labelIcon", "helperText", "errorText", "errorIconAriaLabel", "error", "successText", "successIconAriaLabel", "partial", "name", "dir", "className", "onChange", "inCheckboxGroup"];
var Checkbox = forwardRef(function (_ref, ref) {
  var id = _ref.id,
      label = _ref.label,
      ariaLabel = _ref['aria-label'],
      decoration = _ref.decoration,
      secondaryText = _ref.secondaryText,
      labelIcon = _ref.labelIcon,
      helperText = _ref.helperText,
      errorText = _ref.errorText,
      _ref$errorIconAriaLab = _ref.errorIconAriaLabel,
      errorIconAriaLabel = _ref$errorIconAriaLab === void 0 ? 'Error' : _ref$errorIconAriaLab,
      error = _ref.error,
      successText = _ref.successText,
      _ref$successIconAriaL = _ref.successIconAriaLabel,
      successIconAriaLabel = _ref$successIconAriaL === void 0 ? 'Success' : _ref$successIconAriaL,
      partial = _ref.partial,
      _ref$name = _ref.name,
      name = _ref$name === void 0 ? 'default' : _ref$name,
      dir = _ref.dir,
      className = _ref.className,
      onChange = _ref.onChange,
      inCheckboxGroup = _ref.inCheckboxGroup,
      rest = _objectWithoutProperties(_ref, _excluded);

  var tokens = useTheme();
  var checkboxStyles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  var cssVal = [checkboxStyles.checkboxBase, error && checkboxStyles.checkboxErrorState, inCheckboxGroup && checkboxStyles.groupBase, inCheckboxGroup && secondaryText && checkboxStyles.groupBaseWithSecText];

  var checkboxTextItemsElm = function () {
    var thumbnail = decoration && jsx("span", {
      css: checkboxStyles.itemThumbnail,
      "data-ppui": "true"
    }, jsx(ClassNames, {
      "data-ppui": "true"
    }, function (_ref2) {
      var css = _ref2.css,
          cx = _ref2.cx;
      return React.cloneElement(decoration, {
        className: cx(decoration.props.className, css(checkboxStyles.itemThumbnailDecoration))
      });
    }));
    var itemText = jsx("span", {
      css: checkboxStyles.itemText,
      "data-ppui": "true"
    }, jsx(BodyText, {
      as: "span",
      css: [!label && checkboxStyles.screenReader],
      "data-ppui": "true"
    }, label !== null && label !== void 0 ? label : ariaLabel), secondaryText && jsx(CaptionText, {
      css: checkboxStyles.itemSecondaryText,
      as: "span",
      "data-ppui": "true"
    }, secondaryText));
    return jsx("span", {
      css: checkboxStyles.checkboxItems,
      "data-ppui": "true"
    }, thumbnail, itemText);
  };

  var checkboxId = id !== null && id !== void 0 ? id : uniqueId('Checkbox_');
  var checkboxErrorIcon = jsx(CriticalIcon, {
    css: checkboxStyles.errorIcon,
    size: "sm",
    role: "img",
    "aria-label": errorIconAriaLabel,
    "data-ppui": "true"
  });
  var checkboxSuccessIcon = jsx(CheckmarkBackgroundIcon, {
    css: checkboxStyles.successIcon,
    size: "sm",
    role: "img",
    "aria-label": successIconAriaLabel,
    "data-ppui": "true"
  });
  var message;

  if (error && !successText) {
    message = errorText && jsx("p", {
      css: checkboxStyles.checkboxMessageText,
      id: name,
      "data-ppui": "true"
    }, checkboxErrorIcon, errorText);
  } else if (successText && !error) {
    message = successText && jsx("p", {
      css: checkboxStyles.checkboxMessageText,
      id: name,
      "data-ppui": "true"
    }, checkboxSuccessIcon, successText);
  }

  return jsx("div", {
    css: cssVal,
    className: className,
    "data-id": "checkbox",
    dir: dir,
    "data-ppui-info": "checkbox_4.6.3"
  }, jsx("input", _extends({
    css: [checkboxStyles.checkboxInput, error && checkboxStyles.checkboxErrorState],
    type: "checkbox",
    id: checkboxId,
    name: name,
    ref: ref,
    onChange: function handleChange(evt) {
      // stop the propagation to the parent if onChange exists

      /* istanbul ignore next */
      if (onChange) {
        evt.stopPropagation();
        onChange(evt);
      }
    }
  }, rest, error && errorText && {
    'aria-describedby': name,
    'aria-invalid': true
  }, {
    "data-ppui": "true"
  })), function renderLabel() {
    var _checkboxStyles$check;

    var labelIconElm = labelIcon && jsx("span", {
      css: checkboxStyles.iconLabel,
      "data-ppui": "true"
    }, labelIcon);
    var checkboxCheckIcon = jsx(CheckmarkSmIcon, {
      css: checkboxStyles.checkIcon,
      size: "xs",
      "data-ppui": "true"
    });
    var checkboxPartialIcon = jsx("span", {
      css: checkboxStyles.partialIcon,
      "data-ppui": "true"
    });
    var helperTextElm = !error && !successText && helperText && jsx("p", {
      css: checkboxStyles.helper,
      "data-ppui": "true"
    }, helperText);
    var iconContainerCssVal = [(_checkboxStyles$check = checkboxStyles.checkIconContainer) === null || _checkboxStyles$check === void 0 ? void 0 : _checkboxStyles$check.call(checkboxStyles, !!decoration), error && checkboxStyles.errorCheckIconContainer];
    var labelContainerCssVal = [checkboxStyles.checkboxLabel, (error || successText) && !inCheckboxGroup && checkboxStyles.labelError];
    var checkboxItemsIcon = partial ? checkboxPartialIcon : checkboxCheckIcon;
    var checkboxItemsElm = jsx("span", {
      css: checkboxStyles.checkboxItems,
      "data-ppui": "true"
    }, jsx("span", {
      css: iconContainerCssVal,
      "data-ppui": "true"
    }, checkboxItemsIcon), checkboxTextItemsElm());
    return jsx("label", {
      css: labelContainerCssVal,
      htmlFor: checkboxId,
      "data-ppui": "true"
    }, checkboxItemsElm, labelIconElm, helperTextElm);
  }(), message);
});

var _excluded$1 = ["id", "name", "label", "links", "onChange", "errorText", "errorIconAriaLabel", "className", "children"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var LegalConsent = forwardRef(function (_ref, ref) {
  var id = _ref.id,
      name = _ref.name,
      label = _ref.label,
      _ref$links = _ref.links,
      links = _ref$links === void 0 ? [] : _ref$links,
      onChange = _ref.onChange,
      errorText = _ref.errorText,
      _ref$errorIconAriaLab = _ref.errorIconAriaLabel,
      errorIconAriaLabel = _ref$errorIconAriaLab === void 0 ? 'Error' : _ref$errorIconAriaLab,
      className = _ref.className,
      children = _ref.children,
      rest = _objectWithoutProperties(_ref, _excluded$1);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var linkComponents = links.map(function (props, index) {
    var link = React.createElement(Link, _objectSpread(_objectSpread({}, props), {}, {
      size: 'sm'
    }));
    return jsx("li", {
      key: index,
      "data-ppui": "true"
    }, link);
  });
  var checkboxId = id !== null && id !== void 0 ? id : uniqueId('LegalConsent_');
  var cssVal = [_styles.checkboxBase, errorText && _styles.checkboxErrorState];
  var legalConsentSupportingTextWrapperCssVal = [_styles.legalConsentSupportingTextWrapper, label && errorText && _styles.labelError, !(label === null || label === void 0 ? void 0 : label.length) && _styles.legalConsentSupportingTextWrapperWhenNoLabel];
  var legalConsentLinksWrapperCssVal = [_styles.legalConsentGroupSpacing, _styles.legalConsentLinksWrapper, !(label === null || label === void 0 ? void 0 : label.length) && _styles.legalConsentLinksWrapperWhenNoLabel];
  var componentMarkup;
  var Tag = 'p';

  if (linkComponents.length) {
    componentMarkup = jsx("ul", {
      css: legalConsentLinksWrapperCssVal,
      "data-ppui": "true"
    }, linkComponents);
    Tag = 'div';
  } else {
    componentMarkup = children;
  }

  return jsx("div", {
    css: cssVal,
    className: className,
    "data-ppui-info": "checkbox_4.6.3"
  }, label && jsx(React.Fragment, null, jsx("input", _extends({
    css: [_styles.checkboxInput, errorText && _styles.checkboxErrorState],
    type: "checkbox",
    id: checkboxId,
    name: name,
    ref: ref,
    onChange: function handleChange(evt) {
      // stop the propagation to the parent if onChange exists

      /* istanbul ignore next */
      if (onChange) {
        evt.stopPropagation();
        onChange(evt);
      }
    }
  }, rest, errorText && {
    'aria-describedby': name,
    'aria-invalid': true
  }, {
    "data-ppui": "true"
  })), function renderLabel() {
    var _styles$checkIconCont;

    var iconContainerCssVal = [(_styles$checkIconCont = _styles.checkIconContainer) === null || _styles$checkIconCont === void 0 ? void 0 : _styles$checkIconCont.call(_styles, false), errorText && _styles.errorCheckIconContainer];
    var checkboxItemsElm = jsx("span", {
      "data-ppui": "true"
    }, jsx("span", {
      css: iconContainerCssVal,
      "data-ppui": "true"
    }, jsx(CheckmarkSmIcon, {
      css: _styles.checkIcon,
      size: "xs",
      "data-ppui": "true"
    })), jsx(CaptionText, {
      css: _styles.legalConsentText,
      as: "span",
      "data-ppui": "true"
    }, label));
    var labelContainerCssVal = [_styles.checkboxLabel, _styles.checkboxItems, _styles.legalConsentLabel];
    return jsx("label", {
      css: labelContainerCssVal,
      htmlFor: checkboxId,
      "data-ppui": "true"
    }, checkboxItemsElm);
  }()), jsx(Tag, {
    css: legalConsentSupportingTextWrapperCssVal,
    "data-ppui": "true"
  }, componentMarkup), label && errorText && jsx("p", {
    css: _styles.checkboxMessageText,
    id: name,
    "data-ppui": "true"
  }, jsx(CriticalIcon, {
    css: _styles.errorIcon,
    size: "sm",
    role: "img",
    "aria-label": errorIconAriaLabel,
    "data-ppui": "true"
  }), errorText));
});

var _excluded$2 = ["children", "className"];

var LegalConsentGroup = function (_ref) {
  var children = _ref.children,
      className = _ref.className,
      rest = _objectWithoutProperties(_ref, _excluded$2);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  return jsx("div", _extends({
    css: _styles.legalConsentGroupSpacing,
    className: className
  }, rest, {
    "data-ppui": "true"
  }), children);
};

var _excluded$3 = ["label", "labelIcon", "helperText", "errorText", "error", "successText", "className", "children"];

// eslint-disable-next-line complexity
var CheckboxGroup = function (_ref) {
  var label = _ref.label,
      labelIcon = _ref.labelIcon,
      helperText = _ref.helperText,
      errorText = _ref.errorText,
      error = _ref.error,
      successText = _ref.successText,
      className = _ref.className,
      children = _ref.children,
      rest = _objectWithoutProperties(_ref, _excluded$3);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var isError = error && !successText;
  var isSuccess = successText && !error;
  var checkboxErrorIcon = jsx(CriticalIcon, {
    css: _styles.errorIcon,
    size: "sm",
    "data-ppui": "true"
  });
  var checkboxSuccessIcon = jsx(CheckmarkBackgroundIcon, {
    css: _styles.successIcon,
    size: "sm",
    "data-ppui": "true"
  });
  var helperCssVal = [!error && _styles.helper, (isError || isSuccess) && _styles.checkboxMessageText, _styles.groupHelper];
  var message = (errorText || helperText || successText) && jsx("p", {
    css: helperCssVal,
    "data-ppui": "true"
  }, isError && checkboxErrorIcon, isSuccess && checkboxSuccessIcon, isError && errorText, isSuccess && successText, !isError && !isSuccess && helperText);
  var labelIconElm = labelIcon && jsx("span", {
    css: _styles.iconLabel,
    "data-ppui": "true"
  }, labelIcon);
  var childProps = {
    error: error,
    inCheckboxGroup: true
  };
  var checkboxList = React.Children.toArray(children).map(function (child) {
    return React.cloneElement(child, childProps);
  });
  return jsx("fieldset", _extends({
    css: [_styles.checkboxGroup, error && _styles.checkboxErrorState],
    className: className,
    "data-ppui-info": "checkbox_4.6.3"
  }, rest), jsx("legend", {
    css: _styles.groupLabel,
    "data-ppui": "true"
  }, label, labelIconElm), checkboxList, message);
};

export { Checkbox, CheckboxGroup, LegalConsent, LegalConsentGroup, styles as checkboxStyles };
